import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  membershipFormSchema,
  MembershipFormValues,
} from "./schemas/membershipForm.schema";
import {
  useCreateSaccoMembership,
  useUpdateSaccoMembership,
} from "@saccoHooks";
import { useCurrencyList } from "@/hooks/useCurrencyList";
import {SaccoMembership} from "@/@types/sacco.models.ts";

interface MembershipFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  membership?: SaccoMembership;
}

export function MembershipForm({
  open,
  onOpenChange,
  membership,
}: MembershipFormProps) {
  const isEdit = !!membership;
  const createMembership = useCreateSaccoMembership();
  const updateMembership = useUpdateSaccoMembership();
  const {
    data: availableCurrencies,
    isLoading: isLoadingCurrencies,
    error: errorLoadingCurrencies,
  } = useCurrencyList();
  
  const form = useForm<MembershipFormValues>({
    resolver: zodResolver(membershipFormSchema),
    defaultValues: {
      sacco_name: "",
      membership_number: "",
      phone: "",
      email: "",
      address: "",
      notes: "",
      currency: "KES",
    },
  });

  useEffect(() => {
    if (open) {
      form.reset({
        sacco_name: membership?.sacco_name ?? "",
        membership_number: membership?.membership_number ?? "",
        phone: membership?.phone ?? "",
        email: membership?.email ?? "",
        address: membership?.address ?? "",
        notes: membership?.notes ?? "",
        currency: membership?.currency ?? "KES",
      });
    }
  }, [open, membership, form]);

  const isPending = createMembership.isPending || updateMembership.isPending;

  async function onSubmit(values: MembershipFormValues) {
    try {
      if (isEdit) {
        await updateMembership.mutateAsync({ id: membership.id, ...values });
        toast.success("Membership updated successfully");
      } else {
        await createMembership.mutateAsync({
          sacco_name: values.sacco_name,
          membership_number: values.membership_number,
          phone: values.phone || null,
          email: values.email || null,
          address: values.address || null,
          notes: values.notes || null,
          currency: values.currency,
        });
        toast.success("Membership created successfully");
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(
        isEdit ? "Failed to update membership" : "Failed to create membership",
      );
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edit Membership" : "Add Membership"}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="sacco_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Sacco Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. Stima Sacco" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="membership_number"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Membership Number *</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. MEM-001234" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="currency"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Currency *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select currency" />
                      </SelectTrigger>
                    </FormControl>
                    {isLoadingCurrencies && "Loading Currencies"}
                    {!isLoadingCurrencies && !errorLoadingCurrencies ? (
                      <SelectContent>
                        {availableCurrencies.map((c) => (
                          <SelectItem key={c} value={c}>
                            {c}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    ) : (
                      "Failed to load currencies"
                    )}
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone</FormLabel>
                    <FormControl>
                      <Input placeholder="+254 700 000 000" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="sacco@example.com"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Address</FormLabel>
                  <FormControl>
                    <Input placeholder="Physical address" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Any additional notes"
                      className="resize-none"
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isPending}>
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? "Saving..." : isEdit ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
